import { useState } from "react";

import { useRemoveGlobalTrackedFile, useUpdateGlobalTrackedFile } from "../api";
import type { GlobalTrackedFile, UpdateGlobalTrackedFileInput } from "../api";
import ds from "../components/DriveSyncSettingsCard.module.css";
import { driveOpenUrl } from "../utils/driveOpenUrl";
import { describeAdded, describeScope } from "./globalDataLabels";
import { ScopeFields } from "./GlobalDataTargetingFields";
import { GlobalDriveFileStatePill } from "./GlobalDriveFileStatePill";
import { GlobalDriveRetryLink } from "./GlobalDriveRetryLink";
import folderStyles from "./GlobalDriveFolder.module.css";

type FileChanges = Partial<Omit<UpdateGlobalTrackedFileInput, "file_id">>;

export const GlobalDriveTrackedFileRow = ({ file }: { file: GlobalTrackedFile }) => {
  const update = useUpdateGlobalTrackedFile();
  const remove = useRemoveGlobalTrackedFile();
  const [editingScope, setEditingScope] = useState(false);
  const [tenantScope, setTenantScope] = useState("");
  const [landlordScope, setLandlordScope] = useState("");
  const [error, setError] = useState<string | null>(null);
  const synced = file.synced;
  const added = describeAdded(file.added_by, file.added_at);

  const saveFile = async (changes: FileChanges): Promise<boolean> => {
    setError(null);
    try {
      await update.mutateAsync({
        file_id: file.file_id,
        tenant_scope: file.tenant_scope,
        landlord_scope: file.landlord_scope,
        ...changes,
      });
      return true;
    } catch (e) {
      setError((e as Error).message || "Saving the file failed.");
      return false;
    }
  };

  const openScopeEditor = () => {
    setTenantScope(file.tenant_scope ?? "");
    setLandlordScope(file.landlord_scope ?? "");
    setEditingScope(true);
  };

  const onSaveScope = async () => {
    const saved = await saveFile({
      tenant_scope: tenantScope.trim() || null,
      landlord_scope: landlordScope.trim() || null,
    });
    if (saved) setEditingScope(false);
  };

  const onRemove = () => {
    const ok = window.confirm(
      `Remove "${file.drive_path}" from global data?\n\nIts synced copy leaves global data, so no project's learning agents see it any more. Nothing in Google Drive is changed.`,
    );
    if (!ok) return;
    remove.mutate({ file_id: file.file_id });
  };

  return (
    <li className={ds.sourceRowBlock}>
      <div className={ds.sourceRow}>
        <div className={ds.sourceMain}>
          <span className={ds.sourceName}>{file.drive_path}</span>
          {added && <span className={ds.sourceMeta}>{added}</span>}
          <span className={ds.sourceMeta}>
            {describeScope(file.tenant_scope, file.landlord_scope)}
            {!editingScope && (
              <>
                {" "}
                <button type="button" className={ds.linkButton} onClick={openScopeEditor}>
                  Edit scope
                </button>
              </>
            )}
          </span>
        </div>
        <GlobalDriveFileStatePill record={synced} />
        <div className={folderStyles.rowActions}>
          <GlobalDriveRetryLink record={synced} />
          <a
            className={ds.linkButton}
            href={driveOpenUrl(file.file_id)}
            target="_blank"
            rel="noreferrer"
          >
            Open in Drive
          </a>
          <button
            type="button"
            className={ds.dangerButton}
            onClick={onRemove}
            disabled={remove.isLoading}
          >
            Remove
          </button>
        </div>
      </div>

      <div className={`${ds.sourceTree} ${folderStyles.rowDetails}`}>
        {editingScope && (
          <div className={folderStyles.scopeEditor}>
            <ScopeFields
              subject="file"
              tenantScope={tenantScope}
              landlordScope={landlordScope}
              onTenantScopeChange={setTenantScope}
              onLandlordScopeChange={setLandlordScope}
            />
            <div className={ds.actions}>
              <button
                type="button"
                className={ds.primaryButton}
                onClick={() => void onSaveScope()}
                disabled={update.isLoading}
              >
                {update.isLoading ? "Saving…" : "Save scope"}
              </button>
              <button
                type="button"
                className={ds.secondaryButton}
                onClick={() => setEditingScope(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className={ds.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </li>
  );
};
